import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getSystemConfig, getLogos } from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Wallet, User, LogOut, Settings, Home, History, DollarSign, UserCircle, Users, BarChart3, Shield, Store, ShieldCheck, Download, Printer, Menu, Percent } from 'lucide-react';
import { toast } from 'sonner';
import InstallPWA from '@/components/InstallPWA';
import BalanceNotification from '@/components/BalanceNotification';
import PrinterSettings from '@/components/PrinterSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get role icon and color
const getRoleConfig = (role) => {
  const config = {
    admin: { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Admin' },
    sub_admin: { icon: ShieldCheck, color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Sub-Admin' },
    revendedor: { icon: Store, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Revendedor' },
    api_client: { icon: ShieldCheck, color: 'text-emerald-700', bgColor: 'bg-emerald-100', label: 'API Client' },
    cliente: { icon: User, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Cliente' }
  };
  return config[role] || config.cliente;
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subadminCanRequestBalance, setSubadminCanRequestBalance] = useState(false);
  const [navbarLogo, setNavbarLogo] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);

  useEffect(() => {
    // Load config for sub-admin to check if they can request balance
    if (user?.role === 'sub_admin') {
      getSystemConfig()
        .then(config => setSubadminCanRequestBalance(config.subadmin_can_request_balance || false))
        .catch(() => setSubadminCanRequestBalance(false));
    }
    
    // Load custom navbar logo
    getLogos()
      .then(logos => {
        if (logos.navbar_logo) {
          setNavbarLogo(`${BACKEND_URL}/api/uploads/logos/${logos.navbar_logo}`);
        } else {
          setNavbarLogo('/recargameya-logo.jpg');
        }
      })
      .catch(() => {
        setNavbarLogo('/recargameya-logo.jpg');
      });
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const roleConfig = getRoleConfig(user?.role);
  const RoleIcon = roleConfig.icon;

  const isSubAdmin = user?.role === 'sub_admin';
  const isApiClient = user?.role === 'api_client';

  // Build nav items dynamically
  const navItems = [
    { path: '/dashboard', label: 'Inicio', icon: Home },
  ];
  
  // Only show Recargar if not sub-admin OR if sub-admin has permission.
  // Nunca lo mostramos al api_client — su balance se recarga por admin.
  if ((!isSubAdmin || (isSubAdmin && subadminCanRequestBalance)) && !isApiClient) {
    navItems.push({ path: '/recharge-balance', label: 'Recargar', icon: DollarSign });
  }
  
  navItems.push({ path: '/history', label: 'Historial', icon: History });
  // Reportes solo para roles operativos; el api_client no lo necesita.
  if (!isApiClient) {
    navItems.push({ path: '/reports', label: 'Reportes', icon: BarChart3 });
  }
  // Vista de comisiones por servicio — solo clientes y revendedores (los que ganan).
  if (user?.role === 'cliente' || user?.role === 'revendedor') {
    navItems.push({ path: '/mis-comisiones', label: 'Mis Comisiones', icon: Percent });
  }

  if (user?.role === 'revendedor') {
    navItems.push({ path: '/reseller', label: 'Mis Clientes', icon: Users });
  }

  if (user?.role === 'admin' || user?.role === 'sub_admin') {
    navItems.push({ path: '/admin', label: 'Admin', icon: Settings });
  }

  return (
    <nav className="glass border-b border-slate-200 sticky top-0 z-50 backdrop-blur-xl" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => navigate('/dashboard')}
            data-testid="navbar-logo"
          >
            {navbarLogo && (
              <img 
                src={navbarLogo} 
                alt="RecargameYa" 
                className={`h-[70px] w-auto rounded-md object-contain transition-opacity duration-200 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLogoLoaded(true)}
                onError={(e) => { e.target.src = '/recargameya-logo.jpg'; setLogoLoaded(true); }}
              />
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? 'default' : 'ghost'}
                  onClick={() => navigate(item.path)}
                  className={isActive ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : ''}
                  data-testid={`navbar-link-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Button>
              );
            })}
          </div>

          {/* Install PWA Button + Balance Notification + User Menu */}
          <div className="flex items-center gap-2">
            {(user?.role === 'admin' || user?.role === 'sub_admin') && (
              <BalanceNotification />
            )}
            <InstallPWA variant="compact" showDismiss={false} />
            
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2 sm:px-4 border sm:border-0 border-slate-200 rounded-full sm:rounded-md hover:bg-slate-50" data-testid="navbar-user-menu" aria-label="Abrir menú">
                <div className={`${roleConfig.bgColor} p-2 rounded-full`}>
                  <RoleIcon className={`w-5 h-5 sm:w-4 sm:h-4 ${roleConfig.color}`} />
                </div>
                <span className="hidden sm:inline">{user?.business_name}</span>
                <Menu className="w-4 h-4 sm:hidden text-slate-600" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center gap-2">
                  <div className={`${roleConfig.bgColor} p-2 rounded-full`}>
                    <RoleIcon className={`w-4 h-4 ${roleConfig.color}`} />
                  </div>
                  <div>
                    <p className="font-semibold">{user?.business_name}</p>
                    <p className={`text-xs ${roleConfig.color} font-medium`}>{roleConfig.label}</p>
                    <p className="text-xs text-slate-400">Cédula: {user?.cedula}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="navbar-profile">
                <UserCircle className="w-4 h-4 mr-2" />
                Mi Perfil
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => setShowPrinterSettings(true)} data-testid="navbar-printer-settings">
                <Printer className="w-4 h-4 mr-2" />
                Configurar Impresora
              </DropdownMenuItem>
              
              {/* Mobile Navigation */}
              <div className="md:hidden">
                <DropdownMenuSeparator />
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      data-testid={`navbar-mobile-${item.label.toLowerCase()}`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </div>
              
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="navbar-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Printer Settings Modal */}
      <PrinterSettings 
        isOpen={showPrinterSettings} 
        onClose={() => setShowPrinterSettings(false)} 
      />
    </nav>
  );
};

export default Navbar;
